import { useEffect } from 'react'
import { cn } from '@/lib/cn'

export type ToastTone = 'info' | 'success' | 'danger' | 'warning'

export interface ToastItem {
  id: string
  title: string
  message?: string
  tone: ToastTone
  durationMs: number
}

const toneStyles: Record<ToastTone, string> = {
  info: 'border-[#2b3139] bg-[#1e2329]',
  success: 'border-accent/40 bg-accent/10',
  danger: 'border-danger/40 bg-danger/10',
  warning: 'border-[#f0b90b]/40 bg-[#f0b90b]/10',
}

const toneAccent: Record<ToastTone, string> = {
  info: 'bg-[#848e9c]',
  success: 'bg-accent',
  danger: 'bg-danger',
  warning: 'bg-[#f0b90b]',
}

export function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[]
  onDismiss: (id: string) => void
}) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-end gap-2 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:p-4"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem
  onDismiss: (id: string) => void
}) {
  useEffect(() => {
    if (toast.durationMs <= 0) return
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.durationMs)
    return () => window.clearTimeout(timer)
  }, [toast.id, toast.durationMs, onDismiss])

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex w-full max-w-sm animate-[toast-in_220ms_ease-out] overflow-hidden rounded border shadow-xl',
        'text-sm text-terminal-fg backdrop-blur-md',
        toneStyles[toast.tone],
      )}
    >
      <span className={cn('w-1 shrink-0', toneAccent[toast.tone])} />
      <div className="min-w-0 flex-1 px-3 py-2.5">
        <p className="truncate font-medium leading-tight">{toast.title}</p>
        {toast.message ? (
          <p className="mt-0.5 text-xs leading-snug text-terminal-muted">
            {toast.message}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 px-2.5 text-terminal-muted transition-colors hover:text-terminal-fg"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  )
}
